import type { FetchedTenantTypes } from "@/Utils/api/sanity/types";
import { translate } from "@/translations/functions/hook";

import { get, sum } from "lodash";
import type { GetCustomersResponse } from "server/dwhApi/customers/models.js";
import type { Customer } from "server/dwhApi/customers/types.js";

import { CATEGORY_NONE, KEY_NUMBERS_COLUMNS } from "./constants";
import { type CategoryRow, SortDir } from "./types";

const getGroupedCustomersByCategory = (customers: Customer[]) =>
  customers.reduce(
    (acc, customer) => {
      const category = customer.commercialCustomerCategory || CATEGORY_NONE;

      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push(customer);

      return acc;
    },
    {} as { [k: string]: Customer[] },
  );

const getCategoriesData = ({
  data,
  tenantCategories,
}: {
  data?: GetCustomersResponse;
  tenantCategories: FetchedTenantTypes | null;
}): CategoryRow[] => {
  if (!data?.customers) {
    return [];
  }

  const groupedCustomersByCategory = getGroupedCustomersByCategory(
    data.customers,
  );

  const categoryData = Object.keys(groupedCustomersByCategory)
    .map((category) => {
      const groupedCustomers = groupedCustomersByCategory[category];
      const sanityCategory = tenantCategories?.find((c) => c.id === category);

      const totalGW = sum(
        groupedCustomers.map((c) => c.totalResidualWasteWeight.quantity),
      );
      const totalWaste = sum(
        groupedCustomers.map((c) => c.totalWasteWeight.quantity),
      );
      const totalRecycled = totalWaste - totalGW;
      const recyclingRate = totalWaste ? (totalRecycled * 100) / totalWaste : 0;

      return {
        id: category,
        nrTenants: groupedCustomers.length,
        name:
          category === CATEGORY_NONE
            ? translate("genericLabels:unassigned")
            : sanityCategory?.name || "",
        general: Number(totalGW.toFixed(0)),
        recycled: Number(totalRecycled.toFixed(0)),
        recyclingRate: Number(recyclingRate.toFixed(1)),
      };
    })
    .filter((c) => c.id !== CATEGORY_NONE || c.general || c.recycled);

  return categoryData;
};

export const getKeyNumbersByCategoryRows = ({
  sortKey,
  sortDir,
  data,
  tenantCategories,
}: {
  sortKey: string;
  sortDir: SortDir;
  data?: GetCustomersResponse;
  tenantCategories: FetchedTenantTypes | null;
}) => {
  const col =
    KEY_NUMBERS_COLUMNS.find((c) => c.key === sortKey) ||
    KEY_NUMBERS_COLUMNS[KEY_NUMBERS_COLUMNS.length - 1];
  const dirMul = sortDir === SortDir.ASC ? 1 : -1;

  const categoriesData = getCategoriesData({ data, tenantCategories });

  return categoriesData.sort((a, b) => {
    const va = get(a, col.key);
    const vb = get(b, col.key);

    return sortKey === "name"
      ? dirMul * String(va).localeCompare(String(vb))
      : dirMul * ((va as number) - (vb as number));
  });
};
